import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { lazify } from "../lazify";
import { inject } from "../ng";
import { useCTBetaFeaturesQuery } from "../preferences";
import { toBoolAttr } from "../utils";
import { Chat } from "./chat";
import classes from "./header.module.css";
import { Label, type LabelProps } from "./label";
import { Menu } from "./menu";
import  MapRegionMenu  from "./map-region-menu";

export function Header() {
	return (
		<header className={classes.header}>
			<Links />
			<Menu />
		</header>
	);
}

type LinkProps = LabelProps & {
	href: string;
	strong?: boolean;
};

function Link(props: LinkProps) {
	const { href } = props;
	const [active, setActive] = useState(location.pathname === href);

	useEffect(() => {
		let timer: NodeJS.Timeout | undefined;

		const cancelUrlUpdate = () => {
			if (timer) {
				clearTimeout(timer);
				timer = undefined;
			}
		};

		const updateUrl = () => {
			timer = undefined;
			setActive(location.pathname === href);
		};

		/**
		 * This is needed because the routes with panels transition back to the root state for a few milliseconds.
		 */
		const delay = window.$config.SLIDE_ANIM_SPEED + 100;

		const detach = inject("$rootScope").$on("$locationChangeSuccess", () => {
			cancelUrlUpdate();
			timer = setTimeout(updateUrl, delay);
		});

		return () => {
			detach();
			cancelUrlUpdate();
		};
	}, [href]);

	useEffect(() => {
		setActive(location.pathname === href);
	}, [location.pathname, href]);

	const handleClick = () => {
		const storedClicks = JSON.parse(localStorage.getItem('firstClick') || '{}');
		if (storedClicks[href] === undefined || storedClicks[href] === true) {
			storedClicks[href] = false;
			localStorage.setItem('firstClick', JSON.stringify(storedClicks));
			window.location.href = href; // This will reload the page
		}else{
			setActive(location.pathname === href);
		}
	};

	return (
		<a
			className={classes.item}
			href={active ? "/app" : props.href}
			data-strong={toBoolAttr(props.strong)}
			data-active={toBoolAttr(active)}
			onClick={handleClick}
		>
			<Label {...props} />
		</a>
	);
}

function Links() {
	const user = inject("UserProvider").getUser();

	let admin = false;
	switch (user.roles[0]) {
		case "ALK Administrator":
		case "ALK Support":
		case "Support":
		case "support":
		case "Company Administrator":
		case "cloudxchange Approver":
			admin = true;
	}

	let isContentTools = false;
	let logo = "";
	let links: JSX.Element | undefined;
	switch (window.$config.id) {
		case "contenttools":
			isContentTools = true;
			logo = "/img/cloud-menu-content-tools.svg";
			links = (
				<>
					<Link href="/app/places" icon="/img/icons/locations.svg">
						Place Sets
					</Link>
					<Link href="/app/modifiers" icon="/img/icons/route-exceptions.svg">
						Route Modifier Sets
					</Link>
					{user.isALKUser && (
						<Link
							href="/app/upgrade"
							icon="/img/icons/route-exception-upgrade.svg"
						>
							Upgrade Modifiers
						</Link>
					)}
				</>
			);
			break;
		case "mapoverrides":
			logo = "/img/cloud-menu-mapexact.svg";
			links = (
				<>
					<Link href="/app/edit" icon="/img/icons/map_override.svg">
						Edit Overrides
					</Link>
					<Link href="/app/newroad" icon="/img/icons/add-link.png">
						Add New Road
					</Link>
					<Link href="/app/overrides" icon="/img/icons/override-report.svg">
						Override List
					</Link>
					{user.isALKUser && (
						<Link href="/app/alledits" icon="/img/icons/Edits_Report.png">
							All Edits
						</Link>
					)}
				</>
			);
			break;
		case "mapsure":
			logo = "/img/cloud-menu-mapsure.svg";
			links = (
				<>
					<Link href="/app/edit" icon="/img/icons/map_override.svg">
						Edit Road
					</Link>
					<Link href="/app/newroad" icon="/img/icons/add-link.png">
						Add New Road
					</Link>
					<Link href="/app/overrides" icon="/img/icons/override-report.svg">
						Edits List
					</Link>
					{user.isALKUser && (
						<Link href="/app/alledits" icon="/img/icons/Edits_Report.png">
							All Edits
						</Link>
					)}
				</>
			);
	}

	return (
		<>
			<Link href="/app" icon={logo} strong>
				{window.$config.title}
			</Link>
			<Link href="/app/search" icon="/img/icons/search.png">
				Search
			</Link>
			{links}
			{isContentTools && user.availableRegions.length > 1 && <MapRegionMenu /> }
			<div className={classes.spacer} aria-hidden="true" />
			<Chat />
			{admin && (
				<Link href="/company" icon="/img/nav-company-admin.svg">
					Admin
				</Link>
			)}
			{isContentTools && <LazyNotificationPanel />}
		</>
	);
}

function LazyNotificationPanel() {
	const { data: canUseCTBetaFeatures } = useQuery(useCTBetaFeaturesQuery);
	if (canUseCTBetaFeatures) {
		return (
			<>
				<Importer />
				{/* @ts-ignore */}
				<places-notification-ui />
				{/* @ts-ignore */}
				<places-importer-response-dialog />
			</>
		);
	}
}

// @ts-ignore
const Importer = lazify(() => import("../importer/importer"));
